import React, { useEffect, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import styled from 'styled-components';
import client from '../sanityClient';
import { feesPageQuery } from '../queries';
import { getStaticPageData, getInitialPageData } from '../utils/staticDataLoader';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.6;
  white-space: pre-line;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  
  th, td {
    border: 1px solid #ddd;
    padding: 0.75rem;
    text-align: left;
  }
  
  th {
    background-color: #f5f7fa;
    color: #2E5984;
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
`;


const Note = styled.div`
  background-color: #f5f7fa;
  border-left: 4px solid #2E5984;
  padding: 1rem;
  margin: 1.5rem 0;
`;

const Fees = () => {
  // Default content to show immediately (no loading state)
  const defaultContent = {
    title: "Fees & Charges",
    subtitle: "Accommodation Pricing and Information",
    introText: "We are required by the Federal Government to publish and provide new residents with information regarding our rooms, services and pricing.",
    chargesList: "Broadly there are three types of charges:",
    tableHeader1: "Description",
    tableHeader2: "DAP",
    tableHeader3: "RAD",
    tableRows: [],
    combinationText: "A resident may choose to pay a combination of part RAD and part DAP for example $400,000 room using $100,000 refundable deposit (RAD) and the remaining $300,000 as a DAP of $69.20 this amount is calculated by applying the maximum permissible interest rate to the RAD amount for the room).",
    importantNote: "Accommodation pricing is negotiable between the resident and the provider. The prices listed above are maximum prices listed as required by legislation.",
    additionalNotesList: "Please note:",
    conclusionText: "For more information about our services and facilities refer to our Information Brochure."
  };

  const [pageData, setPageData] = useState(() => 
    getInitialPageData('feesPage', defaultContent)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        // Try to load static data first
        const staticData = await getStaticPageData('feesPage');
        
        if (staticData) {
          setPageData(staticData);
        } else {
          // Fallback to runtime fetching
          const data = await client.fetch(feesPageQuery);
          if (data) {
            setPageData(data);
          }
        }
      } catch (error) {
        console.error('Error loading fees page data:', error);
        // Keep default content if all fails
      }
    };

    loadData();
  }, []);

  return (
    <div>
      <PageHeader 
        title={pageData ? pageData.title : "Fees & Charges"} 
        subtitle={pageData ? pageData.subtitle : "Accommodation Pricing and Information"}
      />
      
      <PageContainer>
        <Paragraph>
          {pageData ? pageData.introText : "We are required by the Federal Government to publish and provide new residents with information regarding our rooms, services and pricing."}
        </Paragraph>
        
        <Paragraph>
          {pageData ? pageData.chargesList : "Broadly there are three types of charges:"}
        </Paragraph>
        
        {pageData && pageData.tableRows && pageData.tableRows.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{pageData.tableHeader1 || "Description"}</th>
                <th>{pageData.tableHeader2 || "DAP"}</th>
                <th>{pageData.tableHeader3 || "RAD"}</th>
              </tr>
            </thead>
            <tbody>
              {pageData.tableRows.map((row, index) => (
                <tr key={index}>
                  <td style={{ whiteSpace: 'pre-line' }}>{row.description}</td>
                  <td>{row.dap}</td>
                  <td>{row.rad}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        
        <Paragraph>
          {pageData ? pageData.combinationText : "A resident may choose to pay a combination of part RAD and part DAP for example $400,000 room using $100,000 refundable deposit (RAD) and the remaining $300,000 as a DAP of $69.20 this amount is calculated by applying the maximum permissible interest rate to the RAD amount for the room)."}
        </Paragraph>
        
        <Note>
          <strong>Important Note:</strong> {pageData ? pageData.importantNote : "Accommodation pricing is negotiable between the resident and the provider. The prices listed above are maximum prices listed as required by legislation."}
        </Note>
        
        <Paragraph>
          {pageData ? pageData.additionalNotesList : "Please note:"}
        </Paragraph>
        
        <Paragraph>
          {pageData ? pageData.conclusionText : "For more information about our services and facilities refer to our Information Brochure."}
        </Paragraph>
      </PageContainer>
    </div>
  );
};

export default Fees;
